import React, { FC, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import BraintreeWebDropIn, { cardPaymentMethodPayload, PaymentMethodPayload } from 'braintree-web-drop-in';
import Svg from 'svg-inline-react';
import LoadingBraintreeSvg from '@src/svgs/loading-braintree.svg';
import { Button } from '@src/components/Button';
import Checkbox from '@src/components/Checkbox';
import api from '@src/util/api';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import {
  CreatePaymentMethodStatusEnum,
  useCreatePaymentMethodMutationMutation,
  useLogCreatePaymentMethodStatusMutationMutation,
} from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import { hasFields } from '@src/util/object/helpers';
import { captureError } from '@src/util/error';
import { THREED_SECURE_ERRORS } from './constants/threedSecureErrors';
import { AddBraintreePaymentMethodProps } from './props';
import './styles.scss';

export function isCardPaymentMethodPayload(obj: PaymentMethodPayload): obj is cardPaymentMethodPayload {
  return hasFields<cardPaymentMethodPayload>(obj, ['threeDSecureInfo']);
}

export const AddBraintreePaymentMethod: FC<AddBraintreePaymentMethodProps> = ({
  buttonLabel = 'Add Payment Method',
  onPaymentMethodAdded,
}) => {
  const dispatch = useAppDispatch();

  const braintreeContainerRef = useRef<HTMLDivElement | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const [hasAcceptedTOS, setHasAcceptedTOS] = useState(false);

  const [isPaymentMethodRequestable, setIsPaymentMethodRequestable] = useState(false);

  const [braintreeInstance, setBraintreeInstance] = useState<BraintreeWebDropIn.Dropin | null>(null);

  const [createPaymentMethod] = useCreatePaymentMethodMutationMutation({
    onCompleted: () => {
      if (onPaymentMethodAdded) {
        onPaymentMethodAdded();
      }
    },
    onError: () => setErrorFlashMessage(),
  });

  const [logCreatePaymentMethodStatus] = useLogCreatePaymentMethodStatusMutationMutation();

  useEffect(() => {
    async function setupDropIn() {
      if (!braintreeContainerRef.current) {
        dispatch(setErrorFlashMessage());

        return;
      }

      try {
        const clientToken: string = await api('/payment/token');

        const braintreeDropInInstance = await BraintreeWebDropIn.create({
          authorization: clientToken,
          container: braintreeContainerRef.current,
          dataCollector: true,
          paymentOptionPriority: ['card'],
          threeDSecure: true,
        });

        braintreeDropInInstance.on('paymentMethodRequestable', () => {
          setIsPaymentMethodRequestable(true);
        });

        braintreeDropInInstance.on('noPaymentMethodRequestable', () => {
          setIsPaymentMethodRequestable(false);
        });

        setBraintreeInstance(braintreeDropInInstance);

        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);

        setErrorFlashMessage();

        captureError(error);
      }
    }

    const braintreeInstanceTeardown = async () => {
      if (braintreeInstance) {
        await braintreeInstance.teardown();
      }
    };

    setupDropIn();

    return () => {
      braintreeInstanceTeardown();
    };
  }, []);

  const isButtonDisabled = !braintreeInstance || isLoading || !hasAcceptedTOS || !isPaymentMethodRequestable;

  const savePaymentMethod = async () => {
    if (isButtonDisabled) {
      return;
    }

    try {
      setIsLoading(true);

      await logCreatePaymentMethodStatus({ variables: { status: CreatePaymentMethodStatusEnum.PRESSED_SUBMIT } });

      const paymentMethod = await braintreeInstance.requestPaymentMethod({
        threeDSecure: {
          amount: '0.01',
          challengeRequested: true,
        },
      });

      if (
        paymentMethod &&
        isCardPaymentMethodPayload(paymentMethod) &&
        THREED_SECURE_ERRORS[paymentMethod?.threeDSecureInfo?.status]
      ) {
        braintreeInstance.clearSelectedPaymentMethod();

        setErrorFlashMessage(THREED_SECURE_ERRORS[paymentMethod.threeDSecureInfo?.status], true);
        setIsLoading(false);

        await logCreatePaymentMethodStatus({
          variables: {
            additionalInfo: `${
              THREED_SECURE_ERRORS[paymentMethod.threeDSecureInfo?.status]
            }, Braintree Error Code: ${paymentMethod.threeDSecureInfo?.status}`,
            status: CreatePaymentMethodStatusEnum.ERROR,
          },
        });

        return;
      }

      await createPaymentMethod({
        variables: {
          paymentMethodInput: {
            deviceData: paymentMethod.deviceData,
            paymentMethodNonce: paymentMethod.nonce,
          },
        },
      });
    } catch (error) {
      setIsLoading(false);
      setErrorFlashMessage();
      captureError(error);
    }
  };

  return (
    <div className="add-braintree-payment-method">
      <div
        className={classnames(
          'add-braintree-payment-method__braintree-container',
          isLoading && 'add-braintree-payment-method__braintree-container--loading',
        )}
        ref={braintreeContainerRef}
      />

      {isLoading && (
        <div className="add-braintree-payment-method__loading-container">
          <Svg src={LoadingBraintreeSvg} />

          <p className="add-braintree-payment-method__loading-text">Loading...</p>
        </div>
      )}

      <div
        className={classnames(
          'add-braintree-payment-method__form-container',
          isLoading && 'add-braintree-payment-method__form-container--loading',
        )}
      >
        <form autoComplete="off" className="add-braintree-payment-method__form">
          <div className="add-braintree-payment-method__info">
            Please note that your 3D secure popup might indicate a 0.01 EUR payment which will not be charged on your
            card.
          </div>

          <Checkbox
            label={
              <span>
                I accept the
                <a href="/terms" target="_">
                  Terms &amp; Conditions
                </a>
              </span>
            }
            onChange={() => setHasAcceptedTOS(!hasAcceptedTOS)}
            value={hasAcceptedTOS}
            wrapperClassName="add-braintree-payment-method__tos-checkbox"
          />
        </form>

        <Button isDisabled={isButtonDisabled} onClick={savePaymentMethod}>
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};
